/*
 * Copyright © 2026 Medaica, Inc
 *
 * All rights reserved.
 *
 * This code is confidential and proprietary information belonging to Medaica, Inc.
 * Unauthorized copying, distribution, or use of this code, in whole or in part,
 * is strictly prohibited, and may constitute a violation of intellectual property rights.
 *
 * If you have received this code in error, please notify the owner immediately
 * at support@medaica.com and delete this file from your system.
 */

// pcg-viewer canvas: render context.
//
// The prototype's draw/interaction code read ~10 module globals (D, RAW, ENV,
// MURMUR, SCALO, view, state, phaseReviewResolution, analysisQuality(), audio
// element). The render context bundles them into one explicit object so the
// draw functions stay verbatim inside while every input is owned by the React
// wrapper:
//
//   rc.payload            — the guide payload (prototype `D`)
//   rc.core               — a createPcgCore(...) instance (analysis derivations)
//   rc.traces             — { raw, env, murmur, scalo } Float32Arrays (RAW/ENV/MURMUR/SCALO)
//   rc.view / rc.state    — mutable view/state objects (see view.js)
//   rc.canvases           — { wave, minimap, spec } canvas elements (refs, may be null)
//   rc.getPlaybackTime()  — clamped audio currentTime or null (prototype currentPlaybackTime)
//   rc.chrome             — optional UI callbacks (setCwtTitle) for DOM writes the
//                           prototype did with getElementById
//   rc.cache              — per-instance caches (binFreqs; prototype `_binFreqs` global)

import { decSigned, decUnsigned } from "../core/traces"
import { createView, createViewerState } from "./view"

// core-viewer.js 15-19 (canvas-side trace decode; DENOISED stays dropped — dead)
export function decodeCanvasTraces(payload) {
  return {
    raw: payload && payload.raw ? decSigned(payload.raw) : new Float32Array(0),
    env: payload && payload.envelope ? decUnsigned(payload.envelope) : new Float32Array(0),
    murmur: payload && payload.murmur ? decUnsigned(payload.murmur) : new Float32Array(0),
    // The default guide payload emits the scalogram as unavailable (`n_freq: 0`, no samples) or omits it.
    scalo: payload && payload.scalo && payload.scalo.b64 ? decUnsigned(payload.scalo) : new Float32Array(0),
  }
}

export function createRenderContext({ payload, core, canvases, getPlaybackTime, traces, view, state, chrome }) {
  if (!payload) throw new Error("pcg-viewer canvas: payload is required")
  if (!core) throw new Error("pcg-viewer canvas: pcg-core instance is required")
  return {
    payload,
    core,
    traces: traces || decodeCanvasTraces(payload),
    view: view || createView(payload.duration),
    state: state || createViewerState(),
    canvases: canvases || {},
    getPlaybackTime: getPlaybackTime || (() => null),
    chrome: chrome || {},
    cache: {},
  }
}
